import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import Dropdown from '../../components/Dropdown';
import Field from '../../components/Field';
import Icon from '../../components/Icon';
import OtpInput from '../../components/OtpInput';
import { ErrorBanner, InfoBanner } from '../../components/Feedback';
import useForm from '../../hooks/useForm';
import * as v from '../../validation/rules';
import { LOGIN_CODES } from '../../validation/serverCodes';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

const STEP = { LOGIN: 'login', OTP: 'otp', FORGOT: 'forgot', RESET: 'reset', DEVICES: 'devices' };

// Only used when one number is registered for more than one role, which is why the
// first row is a choice not to choose rather than a role.
const SIGN_IN_ROLES = [
  { value: '', label: 'Detect automatically' },
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'SUPPORT_EXECUTIVE', label: 'Support Executive' },
  { value: 'SALES_REP', label: 'Sales Representative' },
  { value: 'TEACHER', label: 'Teacher' },
  { value: 'STUDENT', label: 'Student' },
];

export default function LoginPage() {
  const { user, acceptAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(STEP.LOGIN);
  const [mode, setMode] = useState('password');
  const [showPassword, setShowPassword] = useState(false);
  const [resetVia, setResetVia] = useState('SMS');
  const [otpMeta, setOtpMeta] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // Module 10: set when a sign-in is refused for the device cap, so the user can free a
  // slot instead of being told "no" with nowhere to go.
  const [deviceList, setDeviceList] = useState(null);
  // This step is outside the form, so form.submitting does not cover it — without a
  // guard a double-click signs out two devices instead of one.
  const [freeingSlot, setFreeingSlot] = useState(false);

  // Each step validates only the fields it actually shows, so moving between them
  // never leaves a hidden field blocking submit.
  const validators = {
    [STEP.LOGIN]: mode === 'password' ? { identifier: v.identifier, password: v.required('Password') } : { identifier: v.identifier },
    [STEP.OTP]: { otp: v.otpCode },
    [STEP.FORGOT]: { identifier: v.identifier },
    [STEP.RESET]: { newPassword: v.password },
    // Nothing to validate: the credentials were already accepted to get here.
    [STEP.DEVICES]: {},
  }[step];

  const form = useForm({
    initialValues: { identifier: '', password: '', role: '', otp: '', newPassword: '' },
    validators,
  });

  if (user) return <Navigate to={location.state?.from || '/'} replace />;

  const goto = (next) => {
    setError(null);
    setStep(next);
  };

  const handle = (err) => {
    if (!form.applyServerErrors(err, LOGIN_CODES)) setError(err);
  };

  /**
   * Module 10: the sign-in was refused because the account is on every device it is
   * allowed. The guideline asks that this prompt the user to sign a device out rather
   * than silently failing — so the same password that just proved who they are is used
   * to fetch the list and free a slot, and the sign-in is retried for them.
   */
  const offerDeviceChoice = async (values) => {
    const credentials = {
      identifier: values.identifier.trim(),
      password: values.password,
      role: values.role || null,
    };
    setDeviceList(await api.devicesForCredentials(credentials));
    goto(STEP.DEVICES);
  };

  const signOutAndContinue = async (device) => {
    if (freeingSlot) return;
    setFreeingSlot(true);
    setError(null);
    const values = form.values;
    const credentials = {
      identifier: values.identifier.trim(),
      password: values.password,
      role: values.role || null,
    };
    try {
      setDeviceList(await api.signOutDeviceWithCredentials(device.id, credentials));
      acceptAuth(await api.login(credentials));
      navigate(location.state?.from || '/', { replace: true });
    } catch (err) {
      setError(err);
      setFreeingSlot(false);
    }
  };

  const submitLogin = form.handleSubmit(async (values) => {
    setError(null);
    setNotice(null);
    try {
      const role = values.role || null;
      if (mode === 'password') {
        acceptAuth(await api.login({ identifier: values.identifier.trim(), password: values.password, role }));
        navigate(location.state?.from || '/', { replace: true });
      } else {
        const meta = await api.sendOtp({ identifier: values.identifier.trim(), purpose: 'LOGIN', role });
        setOtpMeta({ ...meta, purpose: 'LOGIN' });
        form.setValue('otp', '');
        goto(STEP.OTP);
      }
    } catch (err) {
      if (err.code === 'device_limit_reached') {
        try {
          await offerDeviceChoice(values);
          return;
        } catch (listError) {
          setError(listError);
          return;
        }
      }
      handle(err);
    }
  });

  const submitOtp = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const result = await api.verifyOtp({
        identifier: values.identifier.trim(),
        purpose: otpMeta?.purpose ?? 'LOGIN',
        code: values.otp,
        role: values.role || null,
      });
      if (result.authentication) {
        acceptAuth(result.authentication);
        navigate(location.state?.from || '/', { replace: true });
      } else if (result.passwordResetToken) {
        setResetToken(result.passwordResetToken);
        goto(STEP.RESET);
      }
    } catch (err) {
      if (err.code === 'device_limit_reached') {
        // Signing a device out needs the account password, and a one-time code is not
        // one. Say what will work rather than repeating a refusal they cannot act on.
        setMode('password');
        setNotice('You are signed in on every device you are allowed. Sign in with your '
          + 'password here to choose one to sign out, or sign out from a device you are '
          + 'already using.');
        goto(STEP.LOGIN);
        return;
      }
      handle(err);
    }
  });

  const submitForgot = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const meta = await api.forgotPassword({
        identifier: values.identifier.trim(),
        channel: resetVia,
        role: values.role || null,
      });
      if (resetVia === 'EMAIL') {
        setNotice(`If that account exists, a reset link is on its way to ${meta.maskedDestination}.`);
        goto(STEP.LOGIN);
      } else {
        setOtpMeta({ ...meta, purpose: 'PASSWORD_RESET' });
        form.setValue('otp', '');
        goto(STEP.OTP);
      }
    } catch (err) {
      handle(err);
    }
  });

  const submitReset = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.resetPassword({ resetToken, newPassword: values.newPassword });
      setNotice('Password updated. Sign in with your new password.');
      form.setValue('password', '');
      form.setValue('newPassword', '');
      setResetToken(null);
      goto(STEP.LOGIN);
    } catch (err) {
      handle(err);
    }
  });

  async function resendOtp() {
    setError(null);
    try {
      const meta = await api.sendOtp({
        identifier: form.values.identifier.trim(),
        purpose: otpMeta?.purpose ?? 'LOGIN',
        role: form.values.role || null,
      });
      setOtpMeta({ ...meta, purpose: otpMeta?.purpose ?? 'LOGIN' });
    } catch (err) {
      setError(err);
    }
  }

  const roleField = (
    <Field label="Role" id="role" hint="Needed only if this number is registered for more than one role">
      <Dropdown
        id="role"
        options={SIGN_IN_ROLES}
        value={form.values.role}
        onChange={(role) => form.setValue('role', role)}
      />
    </Field>
  );

  return (
    <AuthLayout>
      {step === STEP.LOGIN && (
        <form {...form.formProps} onSubmit={submitLogin}>
          <h2>Sign in</h2>
          <p className="sub">Mobile or email, then password or a one-time code.</p>
          {notice && <InfoBanner>{notice}</InfoBanner>}
          <ErrorBanner error={error} onDismiss={() => setError(null)} />

          <div className="seg" style={{ marginBottom: 20 }}>
            <label className="seg-opt">
              <input type="radio" name="authmode" checked={mode === 'password'} onChange={() => setMode('password')} />
              <span>Password</span>
            </label>
            <label className="seg-opt">
              <input type="radio" name="authmode" checked={mode === 'otp'} onChange={() => setMode('otp')} />
              <span>OTP</span>
            </label>
          </div>

          <div className="stack">
            <Field label="Mobile number or email" id="identifier" required error={form.errorFor('identifier')}>
              <input
                className="input"
                autoComplete="username"
                placeholder="98765 43210"
                maxLength={255}
                {...form.field('identifier')}
              />
            </Field>

            {mode === 'password' && (
              <Field label="Password" id="password" required error={form.errorFor('password')}>
                <div style={{ position: 'relative' }}>
                  <input
                    className="input"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Min 8 chars, 1 upper, 1 number"
                    style={{ paddingRight: 38 }}
                    maxLength={100}
                    {...form.field('password')}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    style={{ position: 'absolute', right: 2, top: 2, height: 32, width: 32, padding: 0 }}
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    <Icon name={showPassword ? 'eye-off' : 'eye'} size={17} />
                  </button>
                </div>
              </Field>
            )}

            {error?.code === 'role_required' && roleField}

            <div className="row-between">
              <span className="muted tiny">Sessions stay signed in on this device.</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => goto(STEP.FORGOT)}>
                Forgot password?
              </button>
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={form.submitting}>
              {form.submitting ? 'Working…' : mode === 'otp' ? 'Send OTP' : 'Sign in'}
              <Icon name="arrow" size={15} />
            </button>
          </div>

          <hr className="hr" style={{ margin: '26px 0 18px' }} />
          {/* Not `tiny muted` any more. This line is the only route a new teacher or
              student has into the product, so setting it in the smallest type on the
              page put the one thing they need where it reads as a footnote. */}
          <p className="auth-alt">
            New teacher? <Link to="/register/teacher">Register</Link> · Student?{' '}
            <Link to="/register/student">Create an account</Link>
          </p>
        </form>
      )}

      {step === STEP.DEVICES && (
        <div>
          <button type="button" className="btn btn-ghost" style={{ marginBottom: 16, paddingLeft: 0 }}
                  onClick={() => { setDeviceList(null); setFreeingSlot(false); goto(STEP.LOGIN); }}>
            <Icon name="back" size={15} />
            Back to sign in
          </button>
          <h2>Sign out a device to continue</h2>
          <p className="sub">
            Your account can be signed in on {deviceList?.limit} device
            {deviceList?.limit === 1 ? '' : 's'} at a time, and all of them are in use.
            Choose one to sign out — you will be signed in here straight after.
          </p>
          <ErrorBanner error={error} onDismiss={() => setError(null)} />

          <ul className="plain-list">
            {(deviceList?.devices ?? []).map((device) => (
              <li key={device.id} className="row-between device-row">
                <div>
                  <div className="device-name">{device.deviceLabel}</div>
                  <div className="tiny muted">
                    Signed in {new Date(device.signedInAt).toLocaleString()}
                  </div>
                </div>
                <button type="button" className="btn btn-secondary btn-sm"
                        disabled={freeingSlot}
                        onClick={() => signOutAndContinue(device)}>
                  {freeingSlot ? 'Working…' : 'Sign out'}
                </button>
              </li>
            ))}
          </ul>

          {(deviceList?.devices ?? []).length === 0 && (
            <InfoBanner>
              Nothing is signed in any more — the sessions may have just expired. Go back
              and sign in again.
            </InfoBanner>
          )}
        </div>
      )}

      {step === STEP.OTP && (
        <form {...form.formProps} onSubmit={submitOtp}>
          <button type="button" className="btn btn-ghost" style={{ marginBottom: 16, paddingLeft: 0 }}
                  onClick={() => goto(STEP.LOGIN)}>
            <Icon name="back" size={15} />
            Change number
          </button>
          <h2>Enter the code</h2>
          <p className="sub">
            Sent to {otpMeta?.maskedDestination ?? form.values.identifier}. Six digits, valid for{' '}
            {Math.round((otpMeta?.expiresInSeconds ?? 600) / 60)} minutes.
          </p>
          <ErrorBanner error={error} onDismiss={() => setError(null)} />
          {otpMeta?.devCode && (
            <InfoBanner>
              Development mode — your code is <strong>{otpMeta.devCode}</strong>
            </InfoBanner>
          )}

          <Field id="otp" error={form.errorFor('otp')}>
            <OtpInput
              value={form.values.otp}
              invalid={form.showError('otp')}
              onChange={(code) => form.setValue('otp', code)}
              onComplete={() => form.markTouched('otp')}
            />
          </Field>

          <div className="row-between" style={{ margin: '12px 0 20px' }}>
            <span className="muted">Didn&apos;t get it?</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={resendOtp}>
              Resend code
            </button>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={form.submitting}>
            Verify &amp; continue
            <Icon name="check" size={15} />
          </button>
        </form>
      )}

      {step === STEP.FORGOT && (
        <form {...form.formProps} onSubmit={submitForgot}>
          <button type="button" className="btn btn-ghost" style={{ marginBottom: 16, paddingLeft: 0 }}
                  onClick={() => goto(STEP.LOGIN)}>
            <Icon name="back" size={15} />
            Back to sign in
          </button>
          <h2>Reset password</h2>
          <p className="sub">
            We&apos;ll send a 6-digit code to your registered mobile, or a reset link to your email.
          </p>
          <ErrorBanner error={error} onDismiss={() => setError(null)} />

          <div className="stack">
            <Field label="Registered mobile or email" id="identifier" required error={form.errorFor('identifier')}>
              <input className="input" autoComplete="username" placeholder="98765 43210" maxLength={255}
                     {...form.field('identifier')} />
            </Field>
            <div style={{ display: 'flex', gap: 16, padding: '4px 0' }}>
              <label className="radio">
                <input type="radio" name="resetmode" checked={resetVia === 'SMS'} onChange={() => setResetVia('SMS')} />
                <span className="dot" />
                SMS code
              </label>
              <label className="radio">
                <input type="radio" name="resetmode" checked={resetVia === 'EMAIL'} onChange={() => setResetVia('EMAIL')} />
                <span className="dot" />
                Email link
              </label>
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={form.submitting}>
              {form.submitting ? 'Sending…' : 'Send reset instructions'}
              <Icon name="arrow" size={15} />
            </button>
          </div>
        </form>
      )}

      {step === STEP.RESET && (
        <form {...form.formProps} onSubmit={submitReset}>
          <h2>Choose a new password</h2>
          <p className="sub">Resetting signs you out of every other device.</p>
          <ErrorBanner error={error} onDismiss={() => setError(null)} />
          <div className="stack">
            <Field label="New password" id="newPassword" required hint={v.PASSWORD_HINT}
                   error={form.errorFor('newPassword')}>
              <input className="input" type="password" autoComplete="new-password" maxLength={100}
                     {...form.field('newPassword')} />
            </Field>
            <button type="submit" className="btn btn-primary btn-block" disabled={form.submitting}>
              {form.submitting ? 'Saving…' : 'Set password'}
              <Icon name="check" size={15} />
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
